import {
  ApiPaginatedResponse,
  ApiQuery,
} from '@kinet-open-network/client/core/api-client';
import { BaseModel } from '@kinet-open-network/client/shared/data-access/model';
import { AbilitySubject } from '@kinet-open-network/shared/data-access/model';
import { map, Observable } from 'rxjs';
import { FormlyMultiselectLazyFieldConfig } from '../types/multiselect-lazy/multiselect-lazy-type.component';
import { FormlySelectLazyFieldConfig } from '../types/select-lazy/select-lazy-type.component';
import { FieldBuilderBase, FormlyPartailConfig } from './field-builder-base';

const buildQueryFn =
  <T>(
    service: {
      query: (
        q?: ApiQuery | null,
        model?: any,
      ) => Observable<ApiPaginatedResponse<T>>;
    },
    fields?: string[] | '*',
    labelProp?: keyof T,
    mapLabel?: (item: T) => string,
    forceFilters?: string,
    mapChips?: (item: T) => { label: string; color?: string; icon?: string }[],
  ) =>
  (
    perPage: number,
    page: number,
    search?: string,
    filters?: string,
    model?: any,
  ) =>
    service
      .query(
        {
          perPage,
          page,
          search,
          filters: !forceFilters
            ? filters
            : filters?.length
              ? `(${filters}) AND (${forceFilters})`
              : forceFilters,
          fields: fields === '*' ? undefined : fields,
        },
        model,
      )
      .pipe(
        map((res) => {
          return {
            ...res,
            data: res.data.map((item) => {
              const newItem = { ...item } as any;
              if (mapLabel) {
                newItem[labelProp as string] = mapLabel(item);
              }

              if (mapChips) {
                newItem.chips = mapChips(item);
              }

              return newItem;
            }),
          };
        }),
      );

export class FieldBuilderSelectLazy<
  T,
> extends FieldBuilderBase<FormlySelectLazyFieldConfig> {
  constructor(object?: FormlyPartailConfig<FormlySelectLazyFieldConfig>) {
    super({
      type: 'select-lazy',
      ...object,
    });
  }

  query(
    service: {
      query: (
        q?: ApiQuery | null,
        model?: any,
      ) => Observable<ApiPaginatedResponse<T>>;
    },
    additionalColumns?: (keyof T | string)[] | '*',
    mapLabel?: (item: T | any) => string,
    forceFilters?: string,
    mapChips?: (
      item: T | any,
    ) => { label: string; color?: string; icon?: string }[],
  ) {
    this.props({
      mapLabel,
      query: buildQueryFn(
        service,
        additionalColumns === '*'
          ? '*'
          : [
              this.config.props!.valueProp as string,
              this.config.props!.labelProp as string,
              ...((additionalColumns ?? []) as string[]),
            ],
        this.config.props!.labelProp as keyof T,
        mapLabel,
        forceFilters,
        mapChips,
      ),
    });
    return this;
  }
}

export class FieldBuilderMultiselectLazy<
  T extends BaseModel<unknown, AbilitySubject>,
> extends FieldBuilderBase<FormlyMultiselectLazyFieldConfig> {
  constructor(object?: FormlyPartailConfig<FormlyMultiselectLazyFieldConfig>) {
    super({ type: 'multiselect-lazy', ...object });
  }

  query(
    service: {
      query: (q?: ApiQuery | null) => Observable<ApiPaginatedResponse<T>>;
    },
    additionalColumns?: (keyof T)[],
    mapLabel?: (item: T) => string,
    forceFilters?: string,
  ) {
    this.props({
      mapLabel,
      query: buildQueryFn(
        service,
        [
          this.config.props!.valueProp as string,
          this.config.props!.labelProp as string,
          ...((additionalColumns ?? []) as string[]),
        ],
        this.config.props!.labelProp as keyof T,
        mapLabel,
        forceFilters,
      ),
    });
    return this;
  }
}
